'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { Button } from '@src/components/Button/Button';
import { LinkTile } from '@src/components/LinkTile/LinkTile';
import type { SeeMore } from '@src/components/SeeMoreLink/SeeMoreLink';
import { SeeMoreLink } from '@src/components/SeeMoreLink/SeeMoreLink';
import { SliderV2, slideClassName } from '@src/components/SliderV2/SliderV2';
import { useE2ETesting } from '@src/hooks/useE2ETesting';
import { cocoaProgramToRailItem } from '@src/mappers/program/programs-rail.mapper';
import { useBreakpoints } from '@src/utils/breakpoints.util';
import { cn } from '@src/utils/tailwind';
export type SliderItem = CocoApi.CollectionItem & {
  className?: string;
  dietaryGroups?: CocoApi.DietaryGroup[];
};
interface ProgramsRailProps {
  items: SliderItem[];
  title: string;
  subtitle?: string;
  className?: string;
  seeMoreLink?: SeeMore;
  seeMoreButton?: SeeMore;
  columns?: 2 | 4 | 5;
  openInNewTab?: boolean;
  compact?: boolean;
  onItemSelect?: (item: SliderItem, index: number) => void;
}
export function ProgramsRail(props) {
  const $ = _c(61);
  const {
    columns: t0,
    compact: t1,
    seeMoreLink,
    items,
    title,
    className,
    seeMoreButton,
    subtitle,
    openInNewTab,
    onItemSelect
  } = props;
  const columns = t0 === undefined ? 4 : t0;
  const compact = t1 === undefined ? false : t1;
  const {
    isDesktop
  } = useBreakpoints();
  const {
    testIdPrefix,
    getTestId,
    E2E
  } = useE2ETesting();
  if (!props?.items?.length) {
    return null;
  }
  let t2;
  if ($[0] !== className) {
    t2 = cn("h-full w-full", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== E2E.component.container || $[3] !== getTestId || $[4] !== testIdPrefix) {
    t3 = getTestId(testIdPrefix, E2E.component.container);
    $[2] = E2E.component.container;
    $[3] = getTestId;
    $[4] = testIdPrefix;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !compact && "lg:mb-8";
  let t5;
  if ($[6] !== t4) {
    t5 = cn("m-auto mb-4 flex items-center justify-between pt-0", t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = compact ? "text-heading-5" : "text-heading-display-2 font-bold";
  let t7;
  if ($[8] !== t6) {
    t7 = cn("m-0 text-bodi-white", t6);
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7 || $[11] !== title) {
    t8 = <h2 className={t7}>{title}</h2>;
    $[10] = t7;
    $[11] = title;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== E2E.component.button || $[14] !== getTestId || $[15] !== seeMoreLink || $[16] !== subtitle || $[17] !== testIdPrefix) {
    t9 = (subtitle ?? seeMoreLink) && <div className="flex items-center gap-2">{subtitle && <p className="m-0 text-body-base font-normal text-bodi-white">{subtitle}</p>}{seeMoreLink && <SeeMoreLink {...seeMoreLink} className="lg:hidden" dataTestId={getTestId(testIdPrefix, E2E.component.button, "see-all")} />}</div>;
    $[13] = E2E.component.button;
    $[14] = getTestId;
    $[15] = seeMoreLink;
    $[16] = subtitle;
    $[17] = testIdPrefix;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="flex flex-col gap-2">{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== E2E.component.button || $[23] !== getTestId || $[24] !== seeMoreLink || $[25] !== testIdPrefix) {
    t11 = seeMoreLink && <SeeMoreLink {...seeMoreLink} className="hidden lg:flex" dataTestId={getTestId(testIdPrefix, E2E.component.button, "see-all")} />;
    $[22] = E2E.component.button;
    $[23] = getTestId;
    $[24] = seeMoreLink;
    $[25] = testIdPrefix;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== seeMoreButton) {
    t12 = seeMoreButton?.url && <Link href={seeMoreButton.url}><Button size="small" variant="primary" onClick={seeMoreButton.onClick}>{seeMoreButton.label}</Button></Link>;
    $[27] = seeMoreButton;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t12 || $[32] !== t5) {
    t13 = <div className="relative m-auto"><div className={t5}>{t10}{t11}{t12}</div></div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t5;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const t14 = !isDesktop;
  const t15 = isDesktop && !compact ? 40 : 12;
  let t16;
  if ($[34] !== t15) {
    t16 = {
      perView: "auto",
      spacing: t15
    };
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t16) {
    t17 = {
      rubberband: t14,
      mode: "free-snap",
      slides: t16
    };
    $[36] = t14;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const t18 = !compact;
  let t19;
  if ($[39] !== columns || $[40] !== compact || $[41] !== isDesktop || $[42] !== items || $[43] !== onItemSelect || $[44] !== openInNewTab) {
    let t20;
    if ($[46] !== columns || $[47] !== compact || $[48] !== isDesktop || $[49] !== onItemSelect || $[50] !== openInNewTab) {
      t20 = (item, index) => {
        const linkTileProps = cocoaProgramToRailItem(item);
        return <LinkTile key={item.slug} className={slideClassName} compact={compact} description={linkTileProps.description} href={linkTileProps.href} imageUrl={linkTileProps.imageUrl} violator={linkTileProps.violator} title={linkTileProps.title} layout="column" rounded={columns === 5 || !isDesktop || compact ? "small" : "large"} columns={columns} openInNewTab={openInNewTab} onClick={onItemSelect ? () => onItemSelect(item, index) : undefined} />;
      };
      $[46] = columns;
      $[47] = compact;
      $[48] = isDesktop;
      $[49] = onItemSelect;
      $[50] = openInNewTab;
      $[51] = t20;
    } else {
      t20 = $[51];
    }
    t19 = items.map(t20);
    $[39] = columns;
    $[40] = compact;
    $[41] = isDesktop;
    $[42] = items;
    $[43] = onItemSelect;
    $[44] = openInNewTab;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[52] !== t17 || $[53] !== t18 || $[54] !== t19) {
    t20 = <div className="relative m-auto w-full cursor-grab lg:m-auto"><SliderV2 options={t17} hasArrows={t18}>{t19}</SliderV2></div>;
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== t13 || $[57] !== t2 || $[58] !== t20 || $[59] !== t3) {
    t21 = <div className={t2} data-testid={t3}>{t13}{t20}</div>;
    $[56] = t13;
    $[57] = t2;
    $[58] = t20;
    $[59] = t3;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  return t21;
}